import React from 'react'
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from '@material-ui/core'
import {
    Visibility as ViewIcon,
    LinkOff as UnpairIcon,
    Check as CheckIcon,
    Search as SearchIcon,
} from '@material-ui/icons'

import { ResidentTerm, LeaseDocument } from '../../types'
import { useResidentTermDocumentsTableStyles } from './styles'

interface ResidentTermDocumentsTableProps {
    residentTerm: ResidentTerm
    onViewDocument: (doc: LeaseDocument) => void
    onUnpairDocument: (documentId: number) => void
    onSelectDocument: (documentId: number) => void
    onSearchPair?: () => void
    isLoading?: boolean
    hideSearchButton?: boolean
}

export const ResidentTermDocumentsTable: React.FC<ResidentTermDocumentsTableProps> = ({
    residentTerm,
    onViewDocument,
    onUnpairDocument,
    onSelectDocument,
    onSearchPair,
    isLoading,
    hideSearchButton,
}) => {
    const classes = useResidentTermDocumentsTableStyles()
    const documents = (residentTerm.paired_documents || []).sort((a, b) => {
        const nameA = a.file_name || ''
        const nameB = b.file_name || ''
        return nameA.localeCompare(nameB)
    })

    if (isLoading) {
        return (
            <Box className={classes.loadingState}>
                <CircularProgress size={24} />
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 8 }}
                >
                    Loading documents...
                </Typography>
            </Box>
        )
    }

    if (documents.length === 0) {
        return (
            <Box className={classes.emptyState}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    No documents paired for this resident term.
                </Typography>
                {!hideSearchButton && onSearchPair && (
                    <Button
                        startIcon={<SearchIcon />}
                        onClick={onSearchPair}
                        variant="outlined"
                        color="primary"
                        size="small"
                        style={{ marginTop: 8 }}
                    >
                        Search & Pair Documents
                    </Button>
                )}
            </Box>
        )
    }

    return (
        <Box style={{ width: '100%' }}>
            <TableContainer
                component={Paper}
                className={classes.tableContainer}
            >
                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headerCell}>
                                Resident
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Start
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                End
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Title
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="center"
                            >
                                Pages
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Signature Date
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="center"
                            >
                                ✓
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                align="center"
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((doc: LeaseDocument) => (
                            <TableRow
                                key={doc.id}
                                className={
                                    doc.is_selected
                                        ? classes.selectedRow
                                        : classes.unselectedRow
                                }
                                hover
                            >
                                <TableCell>{doc.resident}</TableCell>
                                <TableCell>{doc.lease_start}</TableCell>
                                <TableCell>{doc.lease_end}</TableCell>
                                <TableCell className={classes.titleCell}>
                                    <Tooltip title={doc.title || 'No title'}>
                                        <span>{doc.title || '-'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    {doc.page_count || '-'}
                                </TableCell>
                                <TableCell>
                                    {(doc as any).signature_date || ''}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.selectCell}
                                >
                                    {doc.is_selected ? (
                                        <CheckIcon
                                            color="primary"
                                            fontSize="small"
                                        />
                                    ) : (
                                        <Tooltip title="Select document">
                                            <IconButton
                                                size="small"
                                                aria-label="Select document"
                                                className={classes.selectButton}
                                                onClick={() =>
                                                    onSelectDocument(doc.id)
                                                }
                                            >
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Box className={classes.actionButtons}>
                                        <Tooltip title="View PDF">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onViewDocument(doc)
                                                }
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Unpair">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onUnpairDocument(doc.id)
                                                }
                                            >
                                                <UnpairIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Search & Pair Button */}
            {!hideSearchButton && onSearchPair && (
                <Box className={classes.searchButton}>
                    <Button
                        startIcon={<SearchIcon />}
                        onClick={onSearchPair}
                        variant="outlined"
                        color="primary"
                        size="small"
                    >
                        Search & Pair Documents
                    </Button>
                </Box>
            )}
        </Box>
    )
}
